import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaExpandAlt } from "react-icons/fa";
import {
  SourcingEventSupplierView,
  TextDocumentDto,
  useListTextDocumentsForNegotiationRoundQuery,
  useListTextDocumentsForSourcingEventQuery,
} from "../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../common/documents/CombinedDocuments";
import { TextDocumentModal } from "../../../../common/documents/TextDocumentModal";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { CompanyPitch } from "./CompanyPitch";
import { useNegotiation } from "./NegotiationContext";

interface Props {
  data: SourcingEventSupplierView;
}

export const EventInfo = ({ data }: Props) => {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const {
    isOpen: isDescriptionModalOpen,
    onOpen: onOpenDescriptionModal,
    onClose: onCloseDescriptionModal,
  } = useDisclosure();
  const { t } = useTranslation();
  const { currentRound } = useNegotiation();

  const { data: eventTextDocRes, isLoading: isLoadingEventTextDocuments } = useListTextDocumentsForSourcingEventQuery({
    eventId: data.id,
  });

  const { data: roundTextDocRes, isLoading: isLoadingRoundTextDocuments } =
    useListTextDocumentsForNegotiationRoundQuery(currentRound ? { negotiationRoundId: currentRound.id } : skipToken);

  const [textDocumentToView, setTextDocumentToView] = useState<TextDocumentDto>();

  const description = currentRound ? currentRound?.description : data.description;
  const descriptionDocuments = currentRound ? currentRound.descriptionDocuments : data.descriptionDocuments;

  const tipTapElement = useMemo(
    () => (
      <TipTap
        content={description ?? ""}
        editable={false}
        hasBorder={false}
        maxHeight=""
        padding="0"
        documents={descriptionDocuments}
      />
    ),
    [description, descriptionDocuments]
  );

  const uploadedDocuments = useMemo(
    () => (currentRound ? currentRound.documents : data.documents),
    [currentRound, data.documents]
  );

  const textDocuments = useMemo(() => {
    return currentRound ? (roundTextDocRes?.documents ?? []) : (eventTextDocRes?.documents ?? []);
  }, [eventTextDocRes?.documents, roundTextDocRes?.documents, currentRound]);

  return (
    <>
      <Modal isOpen={isDescriptionModalOpen} onClose={onCloseDescriptionModal} size="3xl">
        <ModalOverlay />
        <ModalContent maxH="80vh" overflowY="auto">
          <ModalHeader>{t("Description")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box width="full" shadow="inner" rounded="lg" p="4">
              {tipTapElement}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {textDocumentToView && (
        <TextDocumentModal
          textDocument={textDocumentToView}
          onClose={() => setTextDocumentToView(undefined)}
          isUpdating={false}
          editable={false}
        />
      )}
      <Flex width="full" columnGap={5}>
        <Flex width={{ base: "full", md: "50%" }} rounded="md" flexDirection="column">
          <Flex width="full" alignItems="center" justifyContent="space-between" mb="2">
            <Text fontSize={"sm"} fontWeight="semibold">
              {t("Description")}
            </Text>
            <IconButton
              aria-label={t("Expand description")}
              icon={<Icon as={FaExpandAlt} />}
              size="xs"
              variant="ghost"
              onClick={onOpenDescriptionModal}
            />
          </Flex>
          <Box position="relative" width="full" shadow="inner" rounded="lg" pt="0" px="4" pb="2">
            <Box maxHeight={showFullDescription ? undefined : "132px"} overflow="hidden">
              {tipTapElement}
            </Box>
            <Box
              display={showFullDescription ? "none" : "block"}
              position="absolute"
              bottom="0"
              left="0"
              width="full"
              height="4rem"
              p="2"
            >
              <Box width="full" height="full" bgGradient="linear(to-b, transparent, smBackground)"></Box>
            </Box>
            <Button size="sm" variant={"link"} mt="1" onClick={() => setShowFullDescription(!showFullDescription)}>
              {!showFullDescription ? t("Show more") : t("Show less")}
            </Button>
          </Box>
        </Flex>
        <Flex flexDirection="column" width="50%">
          <Text fontSize={"sm"} fontWeight="semibold" pb="2">
            {t("Invited by")}
          </Text>
          <CompanyPitch organization={data.owningOrganization} />
        </Flex>
      </Flex>
      <Box display="flex" flexDirection="column" py="5">
        <Text fontSize={"sm"} fontWeight="semibold" pb="4">
          {t("Documents")}
        </Text>
        <CombinedDocuments
          isLoading={isLoadingEventTextDocuments || isLoadingRoundTextDocuments}
          uploadedDocuments={uploadedDocuments}
          textDocuments={textDocuments}
          isTagsColumnVisible={false}
          isCreatedByColumnVisible={false}
        />
      </Box>
    </>
  );
};
